import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now(), // Unique error ID for tracking
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // In production, you would log this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      const { fallback: CustomFallback, level = 'component' } = this.props;

      if (CustomFallback) {
        return (
          <CustomFallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            level={level}
          />
        );
      }

      // Default error UI based on error level
      if (level === 'app') {
        return <AppLevelError onRetry={this.handleRetry} error={this.state.error} />;
      }

      if (level === 'page') {
        return <PageLevelError onRetry={this.handleRetry} error={this.state.error} />;
      }

      // Component level error (default)
      return <ComponentLevelError onRetry={this.handleRetry} error={this.state.error} />;
    }

    return this.props.children;
  }
}

// App-level error fallback
const AppLevelError = ({ onRetry, error }) => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
      <div className="mb-6">
        <AlertTriangle className="mx-auto h-16 w-16 text-red-500" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
      <p className="text-gray-600 mb-6">
        We're sorry, but the application encountered an unexpected error. Please try refreshing the
        page or contact support if the problem persists.
      </p>
      <div className="space-y-3">
        <button
          onClick={onRetry}
          className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Try Again</span>
        </button>
        <button
          onClick={() => (window.location.href = '/')}
          className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
        >
          <Home size={16} />
          <span>Go Home</span>
        </button>
      </div>
      {error && (
        <details className="mt-4 text-left">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            Error Details
          </summary>
          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto text-red-600">
            {error.toString()}
          </pre>
        </details>
      )}
    </div>
  </div>
);

// Page-level error fallback
const PageLevelError = ({ onRetry, error }) => (
  <div className="min-h-96 flex items-center justify-center px-4">
    <div className="max-w-sm w-full bg-white rounded-lg border border-gray-200 p-6 text-center">
      <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Page Error</h2>
      <p className="text-gray-600 text-sm mb-4">
        This page encountered an error. Please try refreshing or go back.
      </p>
      <button
        onClick={onRetry}
        className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors text-sm flex items-center justify-center space-x-2"
      >
        <RefreshCw size={14} />
        <span>Retry</span>
      </button>
      {process.env.NODE_ENV === 'development' && error && (
        <details className="mt-3 text-left">
          <summary className="cursor-pointer text-xs text-gray-500">Error Details</summary>
          <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-x-auto text-red-600">
            {error.toString()}
          </pre>
        </details>
      )}
    </div>
  </div>
);

// Component-level error fallback
const ComponentLevelError = ({ onRetry, error }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
    <div className="flex items-center space-x-3">
      <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
      <div className="flex-1">
        <h3 className="text-sm font-medium text-red-800">Component Error</h3>
        <p className="text-sm text-red-700 mt-1">This component failed to render properly.</p>
      </div>
      <button
        onClick={onRetry}
        className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200 transition-colors flex items-center space-x-1"
      >
        <RefreshCw size={12} />
        <span>Retry</span>
      </button>
    </div>
    {process.env.NODE_ENV === 'development' && error && (
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-red-600">Error Details</summary>
        <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-x-auto text-red-700">
          {error.toString()}
        </pre>
      </details>
    )}
  </div>
);

// Higher-order component for wrapping components with error boundaries
export const withErrorBoundary = (Component, options = {}) => {
  const WrappedComponent = props => (
    <ErrorBoundary level={options.level || 'component'} fallback={options.fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for error reporting in functional components
export const useErrorHandler = () => {
  const handleError = React.useCallback((error, errorInfo = {}) => {
    console.error('Manual error report:', error, errorInfo);

    // In production, log to error service
    if (process.env.NODE_ENV === 'production') {
      // logErrorToService(error, errorInfo);
    }
  }, []);

  return handleError;
};

export default ErrorBoundary;
